import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AccessDenied, errorStatus } from '@/components/RouteError';
import { usePageHeader } from '@/components/PageHeader';
import { usePrompt } from '@/components/DialogProvider';
import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { QueueTable, queueItemKey, scheduleEntryFromQueueItem } from '@/components/finance/QueueTable';
import { QueueOverview } from '@/components/finance/QueueOverview';
import { ConfirmReceiptDialog } from '@/components/finance/ConfirmReceiptDialog';
import { SettleDialog } from '@/components/finance/SettleDialog';
import { BatchSettle } from '@/components/finance/BatchSettle';
import { apartmentsQuery } from '@/lib/apartments';
import { errorMessage } from '@/lib/errors';
import {
  financeAccountsQuery,
  financeCategoriesQuery,
  financeQueueQuery,
  financeRulesQuery,
  financeSettingsQuery,
  settleStableOperation,
  useInvalidateFinance,
  useMoveFinanceExpected,
  useSkipFinanceExpected,
  useSplitFinanceExpected,
  useStableOperationIds,
} from '@/lib/finance';
import { usePermissions } from '@/lib/me';
import { toMinor } from '@/lib/money';
import type { FinanceQueueItem } from '@/api/generated/types.gen';

const LIMIT = 50;

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export interface QueueSearch {
  direction?: 'in' | 'out';
  origin?: 'rule' | 'operator' | 'submission';
  apartment?: string;
  /** Slice 3 (task 23): narrows to one of the queue's three sources — the Periods screen's close-blocker links land here. */
  source?: 'expected' | 'schedule' | 'provider';
  /** Slice 3: a YYYY-MM month, from a Periods close-blocker link — mapped to due_from/due_to below (the API has no month filter of its own). */
  month?: string;
  page?: number;
}

// The queue's own due_from/due_to for a YYYY-MM month — the API has no
// month filter, only a date range (ListFinanceQueueData's query), so a
// month-scoped link (Periods' close-blocker links) is translated here, once,
// rather than at every call site.
function monthRange(month: string): { dueFrom: string; dueTo: string } {
  const [y, m] = month.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y as number, m as number, 0)).getUTCDate();
  return { dueFrom: `${month}-01`, dueTo: `${month}-${String(lastDay).padStart(2, '0')}` };
}

export const Route = createFileRoute('/_app/finance/queue')({
  // Same shape as apartments.$id.tsx's validateSearch: unknown/malformed
  // values are dropped rather than kept, so a hand-edited or stale URL
  // (e.g. an old bookmark from before a filter's values changed) falls
  // back to "no filter" instead of round-tripping garbage through to the
  // API, which would answer 400 on an unrecognised enum member.
  validateSearch: (s: Record<string, unknown>): QueueSearch => ({
    direction: s.direction === 'in' || s.direction === 'out' ? s.direction : undefined,
    origin:
      s.origin === 'rule' || s.origin === 'operator' || s.origin === 'submission' ? s.origin : undefined,
    apartment: typeof s.apartment === 'string' ? s.apartment : undefined,
    source: s.source === 'expected' || s.source === 'schedule' || s.source === 'provider' ? s.source : undefined,
    month: typeof s.month === 'string' && MONTH_RE.test(s.month) ? s.month : undefined,
    page: typeof s.page === 'number' && Number.isInteger(s.page) && s.page >= 0 ? s.page : undefined,
  }),
  component: QueuePage,
});

// Thin route glue only: Route.useSearch()/Route.useNavigate() resolve
// through the router's OWN matched-route registry, which only exists once
// this file's Route is really part of a mounted router (the file-based
// routeTree) — a test harness that renders QueueScreen directly (the stub
// tree renderWithRouter builds, or a plain render()) has no match for this
// route's id, so those hooks are not usable outside the real app shell.
// Every real behavior — the permission gate very much included — therefore
// lives in QueueScreen below, driven by plain props, so it is exactly as
// testable as QueueTable/SettleDialog/BatchSettle already are.
function QueuePage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <QueueScreen
      search={search}
      onSearchChange={(updater, opts) => navigate({ search: updater, replace: opts?.replace })}
    />
  );
}

export interface QueueScreenProps {
  search: QueueSearch;
  onSearchChange: (updater: (prev: QueueSearch) => QueueSearch, opts?: { replace?: boolean }) => void;
}

export function QueueScreen({ search, onSearchChange }: QueueScreenProps) {
  const { can } = usePermissions();
  const { toast } = useToast();
  const askPrompt = usePrompt();
  const page = search.page ?? 0;
  const range = search.month ? monthRange(search.month) : undefined;
  const filter = {
    direction: search.direction,
    apartmentId: search.apartment,
    origin: search.origin,
    source: search.source,
    dueFrom: range?.dueFrom,
    dueTo: range?.dueTo,
    limit: LIMIT,
    offset: page * LIMIT,
  };
  const queue = useQuery(financeQueueQuery(filter));
  const { data: accounts = [] } = useQuery(financeAccountsQuery());
  const { data: categories = [] } = useQuery(financeCategoriesQuery());
  const { data: apartments = [] } = useQuery(apartmentsQuery);
  const { data: settings } = useQuery(financeSettingsQuery);
  // OH-203: a rule's default account is where its rows' confirmations start (paused rules' rows too).
  const rules = useQuery(financeRulesQuery({ includeDisabled: true })).data?.items;
  const skip = useSkipFinanceExpected();
  const split = useSplitFinanceExpected();
  const move = useMoveFinanceExpected();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [settling, setSettling] = useState<FinanceQueueItem | null>(null);
  const [confirmingSchedule, setConfirmingSchedule] = useState<FinanceQueueItem | null>(null);
  const [batchOpen, setBatchOpen] = useState(false);
  // Fix round 1, item 3 (T23-3, no-tech-debt-drift): onSplit pre-dates task
  // 23 but has the SAME "mints a fresh id every call" defect the review
  // flagged in the newer handlers — a retry after a failed split got a
  // different operation_id. Same fix as finance.events.tsx.
  const opIds = useStableOperationIds();
  // Fix round 2 (Critical): settleStableOperation's own doc comment
  // explains why clearing via mutateAsync's returned promise (not a
  // per-call mutate(vars, {onSuccess}) — one row's split can overlap
  // another's, and the shared `split` hook below only tracks one mutation
  // at a time).
  const invalidate = useInvalidateFinance();

  const canConfirm = can('finance.confirm');
  const items = queue.data?.items ?? [];
  const total = queue.data?.total ?? 0;
  const hasFilters = !!(search.direction || search.origin || search.apartment || search.source || search.month);

  // subtitle ONLY — no `actions` here. usePageHeader's own effect depends
  // solely on `subtitle` (PageHeader.tsx's doc comment: "re-published
  // whenever subtitle changes"), so an `actions` node gated on `selected`
  // would freeze at whatever it was on the render where subtitle last
  // changed and never update again as the operator ticks checkboxes —
  // proven empirically while writing finance.queue.test.tsx (the button
  // never appeared no matter how many rows were selected). The batch
  // button is rendered inline in the toolbar below instead, where a normal
  // re-render keeps it in sync with `selected`.
  usePageHeader({ subtitle: queue.data ? `${total} awaiting` : undefined });

  // Permission gate for the WHOLE screen (spec §14: finance.read). This is
  // the client-side check the coordinator's required mutation targets —
  // remove it and a finance.submit-only actor who navigates straight to
  // /finance/queue (the tab strip never links them here, but the URL still
  // works) sees the real queue instead of this. errorStatus(...) === 403 is
  // kept alongside it as the same belt-and-suspenders the accounts/movements
  // pages already use — a permission revoked mid-session still 403s even if
  // this client-side check somehow passed on stale cached `me` data.
  if (!can('finance.read') || errorStatus(queue.error) === 403) return <AccessDenied />;

  const categoryNames = Object.fromEntries(categories.map((c) => [c.id, c.name]));
  const apartmentNames = Object.fromEntries(apartments.map((a) => [a.id, a.title]));
  // A checked row can leave `items` out from under `selected` — most
  // directly, the operator ticks two rows then settles ONE via its own row
  // Confirm button (not the batch flow): useSettleFinanceExpected's
  // onSuccess invalidates the same query-key prefix the queue lives under
  // (useFinanceMutation, lib/finance.ts), so `items` shrinks on the next
  // render once the refetch lands — but `selected` (plain component state)
  // is never told the row is gone, and without this the toolbar below would
  // keep reading "2 selected" / offering to batch-confirm a row already
  // paid. Skip/Split/Move share the same shape (same invalidation).
  // Filtering on every render — not a useEffect syncing `selected` after
  // the fact — means there is nothing to reconcile and nothing that can
  // drift from what is on screen.
  const itemKeys = new Set(items.map((it) => queueItemKey(it)));
  const effectiveSelected = new Set([...selected].filter((k) => itemKeys.has(k)));
  const hasPrev = page > 0;
  const hasNext = (page + 1) * LIMIT < total;

  async function onSkip(it: FinanceQueueItem) {
    if (!it.target.expected_id) return;
    const reason = await askPrompt({ title: 'Skip this payment?', label: 'Reason' });
    if (!reason?.trim()) return;
    skip.mutate(
      { id: it.target.expected_id, reason: reason.trim() },
      { onError: (e) => toast({ kind: 'error', title: 'Could not skip', message: errorMessage(e) }) },
    );
  }

  async function onSplit(it: FinanceQueueItem) {
    if (!it.target.expected_id) return;
    const expectedId = it.target.expected_id;
    const txt = await askPrompt({
      title: 'Split into parts',
      label: `Parts in ${it.currency}, comma-separated (must sum to the remaining amount)`,
      placeholder: '47.00, 70.00',
    });
    if (!txt) return;
    // Fix round 1, item 6 (review Minor, same shape as RefundActionDialogs'
    // Split): every token must parse — a malformed one now names itself
    // instead of being silently dropped from the sum.
    const tokens = txt.split(',').map((s) => s.trim());
    const parts: number[] = [];
    for (const [i, tok] of tokens.entries()) {
      const minor = toMinor(tok, it.currency);
      if (minor === null || minor <= 0) {
        toast({ kind: 'error', title: `Part ${i + 1} ("${tok}") is not a valid amount` });
        return;
      }
      parts.push(minor);
    }
    if (parts.length < 2) {
      toast({ kind: 'error', title: 'Enter at least two amounts, comma-separated' });
      return;
    }
    const key = `split:${expectedId}`;
    const result = await settleStableOperation(
      split.mutateAsync({ id: expectedId, operationId: opIds.get(key), parts }),
      key,
      opIds,
      invalidate,
    );
    if (!result.ok) toast({ kind: 'error', title: 'Could not split', message: result.message });
  }

  async function onMove(it: FinanceQueueItem) {
    if (!it.target.expected_id) return;
    const d = await askPrompt({ title: 'Move due date', label: 'New date (YYYY-MM-DD)', initialValue: it.due_date });
    if (!d?.trim()) return;
    move.mutate(
      { id: it.target.expected_id, dueDate: d.trim() },
      { onError: (e) => toast({ kind: 'error', title: 'Could not move', message: errorMessage(e) }) },
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-5">
      <header>
        <h2 className="text-2xl font-semibold tracking-tight text-[var(--text-heading)]">Payments to confirm</h2>
        <p className="mt-1 max-w-2xl text-sm text-muted">Review incoming and outgoing payments. Confirm only after the money has moved; refunds waiting for a provider stay open.</p>
      </header>
      {queue.data && !queue.isError && <QueueOverview items={items} total={total} />}
      <div className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">Filter payments</h3>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={() => {
              setSelected(new Set());
              onSearchChange(() => ({}), { replace: true });
            }}>Clear filters</Button>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Select
            label="Direction"
            value={search.direction ?? ''}
            options={[
              { value: '', label: 'All' },
              { value: 'out', label: 'Outgoing' },
              { value: 'in', label: 'Incoming' },
            ]}
            onChange={(e) => {
              const v = e.target.value;
              onSearchChange(
                (p) => ({ ...p, direction: v === 'in' || v === 'out' ? v : undefined, page: 0 }),
                { replace: true },
              );
            }}
          />
          <Select
            label="Origin"
            value={search.origin ?? ''}
            options={[
              { value: '', label: 'All' },
              { value: 'rule', label: 'Rules' },
              { value: 'operator', label: 'Manual' },
              { value: 'submission', label: 'Submissions' },
            ]}
            onChange={(e) => {
              const v = e.target.value;
              onSearchChange(
                (p) => ({
                  ...p,
                  origin: v === 'rule' || v === 'operator' || v === 'submission' ? v : undefined,
                  page: 0,
                }),
                { replace: true },
              );
            }}
          />
          <Select
            label="Apartment"
            value={search.apartment ?? ''}
            options={[{ value: '', label: 'All' }, ...apartments.map((a) => ({ value: a.id, label: a.title }))]}
            onChange={(e) => {
              const v = e.target.value;
              onSearchChange((p) => ({ ...p, apartment: v || undefined, page: 0 }), { replace: true });
            }}
          />
          <Select
            label="Source"
            value={search.source ?? ''}
            options={[
              { value: '', label: 'All' },
              { value: 'expected', label: 'Planned payments' },
              { value: 'schedule', label: 'Booking schedule' },
              { value: 'provider', label: 'Provider payments' },
            ]}
            onChange={(e) => {
              const v = e.target.value;
              onSearchChange(
                (p) => ({
                  ...p,
                  source: v === 'expected' || v === 'schedule' || v === 'provider' ? v : undefined,
                  page: 0,
                }),
                { replace: true },
              );
            }}
          />
        </div>
        {search.month && (
          <p className="mt-3 text-sm text-muted">
            Scoped to {search.month} —{' '}
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => onSearchChange((p) => ({ ...p, month: undefined, page: 0 }), { replace: true })}
            >
              clear
            </button>
          </p>
        )}
      </div>

      {queue.isLoading ? (
        <p role="status" className="text-sm text-muted">Loading payments…</p>
      ) : queue.isError ? (
        <div role="alert" className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
          <p className="font-semibold">Payments could not be loaded</p>
          <p className="mt-1 text-sm text-destructive">{errorMessage(queue.error)}</p>
          <Button className="mt-3" size="sm" variant="secondary" loading={queue.isFetching} onClick={() => void queue.refetch()}>Try again</Button>
        </div>
      ) : (
        <>
          {canConfirm && effectiveSelected.size > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-card)] bg-[var(--surface-sunken)] px-4 py-3 text-sm">
              <span>{effectiveSelected.size} selected</span>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear selection</Button>
                {effectiveSelected.size > 1 ? (
                  <Button size="sm" variant="primary" onClick={() => setBatchOpen(true)}>
                    Confirm {effectiveSelected.size} as one payment
                  </Button>
                ) : (
                  <span className="text-muted">Select another payment to confirm together.</span>
                )}
              </div>
            </div>
          )}
          <QueueTable
            items={items}
            emptyMessage={page > 0 && total > 0 ? 'No payments on this page. Go back to the previous page.' : hasFilters ? 'No payments match these filters.' : undefined}
            canConfirm={canConfirm}
            selected={effectiveSelected}
            onToggle={(k) =>
              // Still React's own functional update (base is `s`, the
              // actual latest state — safe if two toggles somehow land in
              // the same tick, unlike closing over the render-scoped
              // `effectiveSelected` directly), but pruned against itemKeys
              // before applying this toggle: otherwise a ghost key already
              // dropped from what's DISPLAYED (effectiveSelected, Fix 1's
              // whole point) would keep riding along forever inside
              // `selected` itself, and reappear checked the moment its row
              // is back in `items` (a filter change, paging back) — every
              // toggle is also the natural point to let go of ghosts still
              // in state, since it is already about to call setSelected.
              setSelected((s) => {
                const n = new Set([...s].filter((x) => itemKeys.has(x)));
                if (n.has(k)) n.delete(k);
                else n.add(k);
                return n;
              })
            }
            onSettle={setSettling}
            onSkip={(it) => void onSkip(it)}
            onSplit={(it) => void onSplit(it)}
            onMove={(it) => void onMove(it)}
            onConfirmSchedule={setConfirmingSchedule}
            categoryNames={categoryNames}
            apartmentNames={apartmentNames}
          />
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted">
            <span>
              Showing {items.length === 0 ? 0 : page * LIMIT + 1}–{items.length === 0 ? 0 : page * LIMIT + items.length} of {total}
            </span>
            <span className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!hasPrev}
                onClick={() => onSearchChange((p) => ({ ...p, page: page - 1 }))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!hasNext}
                onClick={() => onSearchChange((p) => ({ ...p, page: page + 1 }))}
              >
                Next
              </Button>
            </span>
          </div>
        </>
      )}

      <SettleDialog
        item={settling}
        accounts={accounts}
        categories={categories}
        rules={rules}
        siblingItems={items}
        businessTz={settings?.business_tz}
        onClose={() => setSettling(null)}
      />
      {confirmingSchedule?.booking_id && (
        <ConfirmReceiptDialog
          open
          onClose={() => setConfirmingSchedule(null)}
          bookingId={confirmingSchedule.booking_id}
          entry={scheduleEntryFromQueueItem(confirmingSchedule)}
          businessTz={settings?.business_tz}
        />
      )}
      {batchOpen && (
        <BatchSettle
          items={items.filter((it) => effectiveSelected.has(queueItemKey(it)))}
          accounts={accounts}
          categories={categories}
          rules={rules}
          businessTz={settings?.business_tz}
          onClose={() => {
            setBatchOpen(false);
            setSelected(new Set());
          }}
        />
      )}
    </div>
  );
}
